import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorDimensions,
	CreatorInitialData,
	CreatorOptions,
	CreatorProduct,
	CreatorState,
	CreatorStep,
} from '@/types/creator';
import {
	getAvailableAttributes,
	setAttributesAvailability,
	unSelectUnavailableAttributeValues,
} from '@/utils/creatorUtils';
import { AttributeValue } from '@/api/AttributeValueApi';
import { UUID } from '@/types/types';
import { PricingMethod } from '@/api/ProductTypeApi';

const initialState: CreatorState = {
	creatorProduct: {
		id: null,
		quantity: 1,
		widthInputMode: '',
		heightInputMode: '',
		dimensions: {
			width: undefined,
			height: undefined,
		},
		comment: '',
		price: 0,
		discountedPrice: 0,
	},
	creatorAccessories: [],
	productType: null,
	selectedCategoryId: null,
	selectedFabric: null,
	selectedValues: {},
	availableAttributes: [],
	options: {
		currentStep: CreatorStep.CONFIGURATION,
	},
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		initialize: (state, { payload }: PayloadAction<CreatorInitialData>) => {
			const { productType, product } = payload;

			state.productType = productType;

			state.selectedValues = productType.availableAttributeValues.reduce<
				CreatorState['selectedValues']
			>((acc, attributeValue) => {
				acc[attributeValue.attributeId] = null;
				return acc;
			}, {});

			state.availableAttributes = getAvailableAttributes(productType);

			setAttributesAvailability(state);

			if (product) {
				if (
					productType.pricingMethod === PricingMethod.PER_M2 &&
					product.fabric
				) {
					state.selectedFabric = product.fabric;
					state.selectedCategoryId = product.fabric.categoryId;
				}

				if (product.attributeValues)
					for (const attributeValue of product.attributeValues) {
						state.selectedValues[attributeValue.attributeId] = attributeValue;
					}

				state.creatorAccessories = product.productAccessories;

				state.creatorProduct.quantity = product.quantity;
				state.creatorProduct.widthInputMode = product.widthInputMode;
				state.creatorProduct.heightInputMode = product.heightInputMode;
				state.creatorProduct.dimensions.width = product.width;
				state.creatorProduct.dimensions.height = product.height;
				state.creatorProduct.comment = product.comment;
			}
		},
		selectAttributeValue: (
			state,
			{ payload }: PayloadAction<AttributeValue>
		) => {
			state.selectedValues[payload.attributeId] = payload;
			setAttributesAvailability(state);
			unSelectUnavailableAttributeValues(state);
		},
		updateCreatorProduct: (
			state,
			{ payload }: PayloadAction<Partial<CreatorProduct>>
		) => {
			Object.assign(state.creatorProduct, payload);
		},
		setDimensions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorDimensions>>
		) => {
			Object.assign(state.creatorProduct.dimensions, payload);
		},
		setCreatorProductAccessory: (
			state,
			{
				payload,
			}: PayloadAction<{
				accessoryId: UUID;
				quantity: number;
			}>
		) => {
			const existingAccessory = state.creatorAccessories.find(
				(accessory) => accessory.accessoryId === payload.accessoryId
			);

			if (!payload.quantity)
				state.creatorAccessories = state.creatorAccessories.filter(
					(accessory) => accessory.accessoryId !== payload.accessoryId
				);

			if (existingAccessory) existingAccessory.quantity = payload.quantity;
			else state.creatorAccessories.push(payload);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		navigateCreatorStep: (
			state,
			{ payload }: PayloadAction<'prev' | 'next'>
		) => {
			payload === 'prev'
				? state.options.currentStep--
				: state.options.currentStep++;
		},
		resetCreator: (state) => {
			Object.assign(state, initialState);
		},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
